import { css } from 'styled-components';
import { theme } from './theme';

// フレックスボックスのミックスイン
export const flexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const flexBetween = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const flexColumn = css`
  display: flex;
  flex-direction: column;
`;

export const flexColumnCenter = css`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const flexStart = css`
  display: flex;
  align-items: flex-start;
`;

export const flexEnd = css`
  display: flex;
  align-items: flex-end;
`;

// テキスト省略のミックスイン
export const textEllipsis = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

// 複数行テキスト省略のミックスイン
export const multiLineEllipsis = (lines: number) => css`
  display: -webkit-box;
  -webkit-line-clamp: ${lines};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

// レスポンシブメディアクエリのミックスイン
export const media = {
  mobile: (styles: TemplateStringsArray, ...args: any[]) => css`
    @media (max-width: ${theme.breakpoints.mobile}) {
      ${css(styles, ...args)}
    }
  `,
  tablet: (styles: TemplateStringsArray, ...args: any[]) => css`
    @media (max-width: ${theme.breakpoints.tablet}) {
      ${css(styles, ...args)}
    }
  `,
  desktop: (styles: TemplateStringsArray, ...args: any[]) => css`
    @media (max-width: ${theme.breakpoints.desktop}) {
      ${css(styles, ...args)}
    }
  `,
  largeDesktop: (styles: TemplateStringsArray, ...args: any[]) => css`
    @media (max-width: ${theme.breakpoints.largeDesktop}) {
      ${css(styles, ...args)}
    }
  `,
};

// アニメーションのミックスイン
export const fadeIn = css`
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  animation: fadeIn 0.3s ease-in-out;
`;

export const slideInFromTop = css`
  @keyframes slideInFromTop {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  animation: slideInFromTop 0.3s ease-in-out;
`;

export const slideInFromBottom = css`
  @keyframes slideInFromBottom {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  animation: slideInFromBottom 0.3s ease-in-out;
`;

export const slideInFromLeft = css`
  @keyframes slideInFromLeft {
    from {
      transform: translateX(-20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  animation: slideInFromLeft 0.3s ease-in-out;
`;

export const slideInFromRight = css`
  @keyframes slideInFromRight {
    from {
      transform: translateX(20px);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  animation: slideInFromRight 0.3s ease-in-out;
`;

export const zoomIn = css`
  @keyframes zoomIn {
    from {
      transform: scale(0.95);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
  animation: zoomIn 0.3s ease-in-out;
`;

// ボックスシャドウのミックスイン
export const boxShadow = {
  sm: css`box-shadow: ${theme.shadows.sm};`,
  md: css`box-shadow: ${theme.shadows.md};`,
  lg: css`box-shadow: ${theme.shadows.lg};`,
  xl: css`box-shadow: ${theme.shadows.xl};`,
};

// ボーダーラディウスのミックスイン
export const borderRadius = {
  sm: css`border-radius: ${theme.radii.sm};`,
  md: css`border-radius: ${theme.radii.md};`,
  lg: css`border-radius: ${theme.radii.lg};`,
  xl: css`border-radius: ${theme.radii.xl};`,
  full: css`border-radius: ${theme.radii.full};`,
};

// グラデーションのミックスイン
export const gradients = {
  primary: css`
    background: linear-gradient(135deg, ${theme.colors.primary} 0%, #6BAED6 100%);
  `,
  secondary: css`
    background: linear-gradient(135deg, ${theme.colors.secondary} 0%, #E8E8D0 100%);
  `,
  accent: css`
    background: linear-gradient(135deg, ${theme.colors.accent} 0%, #FF8C7A 100%);
  `,
};

// 透過効果のミックスイン
export const withTransparency = (color: string, opacity: number) => {
  // HEX形式の色をRGBAに変換する関数
  function hexToRgba(hex: string, alpha: number): string {
    // #を削除し、3桁のHEXコードを6桁に拡張
    let cleanHex = hex.replace('#', '');
    if (cleanHex.length === 3) {
      cleanHex = cleanHex.split('').map(char => char + char).join('');
    }
    
    // HEXをRGBに変換
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);
    
    // RGBA形式で返す
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  
  return hexToRgba(color, opacity);
};

// ハイライト効果のミックスイン
export const highlight = css`
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 0.3em;
    background-color: ${withTransparency(theme.colors.secondary, 0.5)};
    z-index: -1;
  }
`; 